"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Sparkles, Calendar, MapPin } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function HeroIntro() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="hero-intro" className="bg-background relative overflow-hidden py-20 md:py-32 lg:py-40">
      {}
      <div className="pointer-events-none absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-secondary/40 blur-3xl opacity-50" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-accent/30 blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-6 flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="outline" className="px-4 py-1.5 rounded-full border-border/60 bg-background/50 backdrop-blur-sm text-muted-foreground tracking-wide uppercase text-[10px] font-medium">
              <Sparkles className="w-3 h-3 mr-2 text-[var(--color-gold-accent)]" />
              Welcome to M/O Collective
            </Badge>

            <div className="space-y-6">
              <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] tracking-tight text-foreground">
                Where community meets <span className="font-medium italic text-[var(--color-green-accent)]">optimization.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl font-light">
                M/O Collective is a curated space designed for connection, education, and wellness. We bring together individuals dedicated to exploring the boundaries of human potential in a supportive, elevated environment.
              </p>
            </div>

            <Separator className="w-12 bg-[var(--color-gold-accent)]/30" />

            <div className="flex flex-col sm:flex-row gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/50">
                  <SafeHydrate><Calendar className="w-4 h-4 text-foreground/70" /></SafeHydrate>
                </div>
                <span className="font-medium">In-store Events</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/50">
                  <MapPin className="w-4 h-4 text-foreground/70" />
                </div>
                <span className="font-medium">Curated Retreats</span>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Link to="/AboutMOPage">
                <Button size="lg" className="rounded-full px-8 h-14 text-base font-medium bg-[var(--color-green-accent)] text-white hover:bg-[var(--color-green-accent)]/90 transition-all duration-300 group">
                  Discover Our Space
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-6 relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/5 border border-border/50">
              <AspectRatio ratio={4 / 5} className="bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo_collective_hero_space.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo_collective_hero_space.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/mo_collective_hero_space.png-wvc-sizes" alt="M/O Collective curated wellness space" className="object-cover w-full h-full hover:scale-105 transition-transform duration-1000 ease-in-out" loading="lazy" />
                {}
                <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-transparent mix-blend-overlay" />
              </AspectRatio>
            </div>
            
            {}
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[var(--color-green-accent)]/10 rounded-full blur-2xl -z-10" />
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-[var(--color-gold-accent)]/10 rounded-full blur-2xl -z-10" />
          </div>

        </div>
      </div>
    </section>;
}